import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { useAuth } from '@/contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  BookOpen, 
  ChevronRight, 
  ChevronDown,
  CheckCircle,
  Circle,
  Play,
  Loader2,
  ArrowLeft,
  Clock,
  FileText,
  Video,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseDetail() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { updateLessonProgress, getCourseProgress } = usePortal();
  
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Fetch course details
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portal/courses/${courseId}`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setCourse(data);
          
          // Check for lesson param in URL
          const lessonParam = searchParams.get('lesson');
          let foundLesson = null;
          
          if (lessonParam) {
            // Find the requested lesson
            for (const mod of data.modules || []) {
              const lesson = mod.lessons?.find(l => l.lesson_id === lessonParam);
              if (lesson) {
                foundLesson = lesson;
                break;
              }
            }
          }
          
          // Select the found lesson or first lesson as default
          if (foundLesson) {
            setSelectedLesson(foundLesson);
          } else if (data.modules?.length > 0 && data.modules[0].lessons?.length > 0) {
            setSelectedLesson(data.modules[0].lessons[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch course:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, token, searchParams]);

  // Fetch progress
  useEffect(() => {
    const fetchProgress = async () => {
      const progressData = await getCourseProgress(courseId);
      if (progressData) {
        setProgress(progressData);
      }
    };

    if (courseId) {
      fetchProgress();
    }
  }, [courseId, getCourseProgress]);

  const isLessonCompleted = (lessonId) => {
    if (!progress?.lessons) return false;
    const lessonProgress = progress.lessons.find(l => l.lesson_id === lessonId);
    return lessonProgress?.completed || false;
  };

  const handleMarkComplete = async () => {
    if (!selectedLesson) return;
    
    setUpdating(true);
    const result = await updateLessonProgress(selectedLesson.lesson_id, true);
    
    if (result.success) {
      // Update local progress state
      const progressData = await getCourseProgress(courseId);
      if (progressData) {
        setProgress(progressData);
      }
    }
    setUpdating(false);
  };

  const handleSelectLesson = (lesson) => {
    setSelectedLesson(lesson);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Course Not Found</h2>
        <Link to="/portal/courses">
          <Button variant="outline">Back to Courses</Button>
        </Link>
      </div>
    );
  }

  const completedCount = progress?.completed_count || 0;
  const totalCount = progress?.total_count || course.lesson_count || 0;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="course-detail-page">
      {/* Back Button */}
      <Link to="/portal/courses" className="inline-flex items-center text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Courses
      </Link>

      {/* Course Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] rounded-2xl p-6 lg:p-8 text-white">
        <div className="flex items-center gap-2 mb-2">
          <span className="bg-white/20 text-sm px-3 py-1 rounded-full">{course.code}</span>
        </div>
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">{course.title}</h1>
        <p className="text-blue-100 mb-4">{course.description}</p>
        
        <div className="bg-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-100">Course Progress</span>
            <span className="font-bold">{progressPercentage}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2 bg-white/20" />
          <p className="text-sm text-blue-200 mt-2">
            {completedCount} of {totalCount} lessons completed
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Modules & Lessons Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Course Content</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Accordion type="multiple" defaultValue={course.modules?.map(m => m.module_id)} className="w-full">
                {course.modules?.map((module, moduleIndex) => (
                  <AccordionItem key={module.module_id} value={module.module_id} className="border-b last:border-b-0">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50">
                      <div className="flex items-center gap-3 text-left">
                        <div className="w-6 h-6 bg-[#1E3A8A] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                          {moduleIndex + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{module.title}</p>
                          <p className="text-xs text-gray-500">{module.lessons?.length || 0} lessons</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className="space-y-1 px-2 pb-2">
                        {module.lessons?.map((lesson) => {
                          const completed = isLessonCompleted(lesson.lesson_id);
                          const isSelected = selectedLesson?.lesson_id === lesson.lesson_id;
                          
                          return (
                            <button
                              key={lesson.lesson_id}
                              onClick={() => handleSelectLesson(lesson)}
                              className={`
                                w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors
                                ${isSelected 
                                  ? 'bg-[#1E3A8A] text-white' 
                                  : 'hover:bg-gray-100 text-gray-700'
                                }
                              `}
                            >
                              {completed ? (
                                <CheckCircle className={`w-4 h-4 shrink-0 ${isSelected ? 'text-green-300' : 'text-green-500'}`} />
                              ) : (
                                <Circle className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white/60' : 'text-gray-300'}`} />
                              )}
                              <span className="text-sm truncate">{lesson.title}</span>
                              {lesson.duration_minutes && (
                                <span className={`text-xs ml-auto shrink-0 ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
                                  {lesson.duration_minutes}m
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Lesson Content */}
        <div className="lg:col-span-2">
          {selectedLesson ? (
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{selectedLesson.title}</CardTitle>
                    {selectedLesson.description && (
                      <p className="text-gray-500 mt-1">{selectedLesson.description}</p>
                    )}
                  </div>
                  {selectedLesson.duration_minutes && (
                    <div className="flex items-center gap-1 text-gray-500 text-sm">
                      <Clock className="w-4 h-4" />
                      {selectedLesson.duration_minutes} min
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* Video */}
                {selectedLesson.video_url && (
                  <div className="mb-6 aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                    <p className="text-white">Video: {selectedLesson.video_url}</p>
                  </div>
                )}

                {/* Content */}
                <article className="prose prose-slate prose-lg max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-h1:text-2xl prose-h1:border-b prose-h1:pb-3 prose-h1:mb-6
                  prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-gray-900
                  prose-blockquote:border-l-4 prose-blockquote:border-[#1E3A8A] prose-blockquote:bg-blue-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:italic prose-blockquote:text-gray-700 prose-blockquote:not-italic prose-blockquote:font-medium
                  prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-[#1E3A8A]
                  prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:overflow-x-auto
                  prose-table:border prose-table:border-collapse
                  prose-th:bg-gray-100 prose-th:border prose-th:border-gray-300 prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold
                  prose-td:border prose-td:border-gray-300 prose-td:px-4 prose-td:py-2
                  prose-ul:my-4 prose-ul:pl-6
                  prose-ol:my-4 prose-ol:pl-6
                  prose-li:my-1 prose-li:text-gray-700
                ">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Custom rendering for code blocks
                      code({ node, inline, className, children, ...props }) {
                        return inline ? (
                          <code className={className} {...props}>{children}</code>
                        ) : (
                          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
                            <code {...props}>{children}</code>
                          </pre>
                        );
                      },
                      // Custom table styling
                      table({ children }) {
                        return (
                          <div className="overflow-x-auto my-6">
                            <table className="min-w-full divide-y divide-gray-300 border border-gray-300">
                              {children}
                            </table>
                          </div>
                        );
                      }
                    }}
                  >
                    {selectedLesson.content || 'No content available'}
                  </ReactMarkdown>
                </article>

                {/* Resources */}
                {selectedLesson.resources?.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <h4 className="font-semibold text-gray-900 mb-3">Resources</h4>
                    <div className="space-y-2">
                      {selectedLesson.resources.map((resource, i) => (
                        <a
                          key={i}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                        >
                          <FileText className="w-5 h-5 text-[#1E3A8A]" />
                          <span className="flex-1 text-sm font-medium">{resource.title}</span>
                          <ExternalLink className="w-4 h-4 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mark Complete Button */}
                <div className="mt-8 pt-6 border-t flex items-center justify-between">
                  {isLessonCompleted(selectedLesson.lesson_id) ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Lesson Completed</span>
                    </div>
                  ) : (
                    <Button 
                      onClick={handleMarkComplete}
                      disabled={updating}
                      className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90"
                    >
                      {updating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark as Complete
                        </>
                      )}
                    </Button>
                  )}

                  {/* Next Lesson Button */}
                  {(() => {
                    const allLessons = course.modules?.flatMap(m => m.lessons || []) || [];
                    const currentIndex = allLessons.findIndex(l => l.lesson_id === selectedLesson.lesson_id);
                    const nextLesson = allLessons[currentIndex + 1];
                    
                    if (nextLesson) {
                      return (
                        <Button 
                          variant="outline"
                          onClick={() => handleSelectLesson(nextLesson)}
                        >
                          Next Lesson
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Select a lesson to begin</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
